import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Search,
  MessageSquare,
  ClipboardCheck,
  FileCheck,
  Truck,
  ChevronRight,
  MapPin,
  Wrench,
  Sparkles,
  Car,
  Shield,
} from "lucide-react";

const ProcessPage = () => {
  // SEO title
  useEffect(() => {
    document.title = "Notre Processus d'Importation | Fast&Car - Comment ça marche ?";
  }, []);

  const steps = [
    {
      number: "01",
      title: "Définition du besoin",
      icon: <MessageSquare className="w-6 h-6" />,
      description:
        "Nous échangeons ensemble pour comprendre précisément vos attentes : marque, modèle, budget, kilométrage, options souhaitées...",
      details: [
        "Entretien téléphonique personnalisé",
        "Analyse de vos critères prioritaires",
        "Définition du budget total",
        "Conseils sur les meilleures options",
      ],
    },
    {
      number: "02",
      title: "Recherche personnalisée",
      icon: <Search className="w-6 h-6" />,
      description:
        "Je scrute les marchés européens et coréens pour dénicher les véhicules correspondant à vos critères. Sélection rigoureuse des meilleures annonces.",
      details: [
        "Recherche sur les marchés internationaux",
        "Présélection des meilleures offres",
        "Vérification des historiques disponibles",
        "Comparatif détaillé des options",
      ],
    },
    {
      number: "03",
      title: "Inspection & Négociation",
      icon: <ClipboardCheck className="w-6 h-6" />,
      description:
        "Contact avec les vendeurs, vérification approfondie du véhicule et négociation du meilleur prix. Mon expertise de Technicien de maintenance des véhicules fait la différence.",
      details: [
        "Échanges directs avec les vendeurs",
        "Inspection technique à distance ou sur place",
        "Négociation du prix d'achat",
        "Rapport d'état détaillé",
      ],
    },
    {
      number: "04",
      title: "Sécurisation & Achat",
      icon: <FileCheck className="w-6 h-6" />,
      description:
        "Validation administrative complète, sécurisation de la transaction et accompagnement jusqu'à la finalisation de l'achat.",
      details: [
        "Vérification des documents",
        "Sécurisation du paiement",
        "Coordination avec le vendeur",
        "Transfert de propriété",
      ],
    },
    {
      number: "05",
      title: "Rapatriement & Livraison",
      icon: <Truck className="w-6 h-6" />,
      description:
        "Organisation du transport depuis le pays d'origine, gestion des formalités douanières et livraison directe à votre domicile ou au siège Fast&Car.",
      details: [
        "Organisation du transport",
        "Formalités douanières (si import)",
        "Immatriculation française",
        "Livraison au lieu convenu",
      ],
    },
  ];

  const servicesAnnexes = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Préparation esthétique",
      description: "Polissage, lustrage, traitement céramique, rénovation des optiques",
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "Entretien courant",
      description: "Vidange, freins, distribution, révision complète",
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Livraison à domicile",
      description: "Livraison partout en France métropolitaine",
    },
    {
      icon: <Car className="w-6 h-6" />,
      title: "Kit jantes & pneus",
      description: "Fourniture et pose de jantes et pneumatiques",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Contrôle technique",
      description: "Passage au contrôle technique avant livraison",
    },
  ];

  return (
    <div data-testid="process-page" className="pt-32 md:pt-40">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Notre Méthode
            </p>
            <h1
              className="font-unbounded text-4xl md:text-6xl font-black text-white leading-tight mb-6"
              data-testid="process-title"
            >
              Un process
              <br />
              <span className="text-neon-lime text-glow">transparent</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-zinc-400 leading-relaxed">
              De la définition de vos besoins à la livraison de votre véhicule,
              découvrez les étapes de notre accompagnement personnalisé.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-[50%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-neon-lime/50 to-transparent" />

            {/* Steps */}
            <div className="space-y-16 md:space-y-24">
              {steps.map((step, index) => (
                <div
                  key={step.number}
                  data-testid={`process-step-${index}`}
                  className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center ${
                    index % 2 === 1 ? "md:direction-rtl" : ""
                  }`}
                >
                  {/* Content */}
                  <div
                    className={`${
                      index % 2 === 1 ? "md:order-2 md:text-right" : "md:order-1"
                    }`}
                  >
                    <div
                      className={`${
                        index % 2 === 1 ? "md:ml-auto" : ""
                      } max-w-lg`}
                    >
                      <div
                        className={`flex items-center gap-4 mb-4 ${
                          index % 2 === 1 ? "md:flex-row-reverse" : ""
                        }`}
                      >
                        <span className="font-mono text-neon-lime text-5xl font-bold opacity-30">
                          {step.number}
                        </span>
                        <div className="w-12 h-12 border border-neon-lime/50 flex items-center justify-center text-neon-lime">
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="font-unbounded text-2xl md:text-3xl font-bold text-white mb-4">
                        {step.title}
                      </h3>
                      <p className="font-manrope text-zinc-400 text-base leading-relaxed mb-6">
                        {step.description}
                      </p>
                      <ul
                        className={`space-y-2 ${
                          index % 2 === 1 ? "md:ml-auto" : ""
                        }`}
                      >
                        {step.details.map((detail, i) => (
                          <li
                            key={i}
                            className={`flex items-center gap-2 text-zinc-500 text-sm ${
                              index % 2 === 1 ? "md:flex-row-reverse" : ""
                            }`}
                          >
                            <div className="w-1.5 h-1.5 bg-neon-lime" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Timeline dot (center) */}
                  <div
                    className={`hidden md:flex items-center justify-center ${
                      index % 2 === 1 ? "md:order-1" : "md:order-2"
                    }`}
                  >
                    <div className="relative">
                      <div className="w-4 h-4 bg-neon-lime transform rotate-45 shadow-[0_0_20px_rgba(57,255,20,0.5)]" />
                      <div className="absolute inset-0 w-4 h-4 bg-neon-lime transform rotate-45 animate-ping opacity-30" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Europe vs Korea Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-y border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Deux Marchés
            </p>
            <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white">
              Europe ou <span className="text-neon-lime">Corée du Sud</span> ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Europe */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 md:p-10" data-testid="comparison-europe">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-4xl">🇪🇺</span>
                <h3 className="font-unbounded text-2xl font-bold text-white">
                  Import Europe
                </h3>
              </div>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                Forfaits structurés et transparents pour l'importation depuis
                l'Allemagne, la Belgique, l'Italie et autres pays européens.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>4 niveaux d'accompagnement</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Prix fixes et transparents</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Sans limite de durée</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Pas de droits de douane</span>
                </li>
              </ul>
              <Link to="/services">
                <Button
                  data-testid="europe-cta"
                  className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-4 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                >
                  <span>Voir les forfaits Europe</span>
                </Button>
              </Link>
            </div>

            {/* Corée du Sud */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 md:p-10" data-testid="comparison-korea">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-4xl">🇰🇷</span>
                <h3 className="font-unbounded text-2xl font-bold text-white">
                  Import Corée du Sud
                </h3>
              </div>
              <p className="text-zinc-400 mb-4 leading-relaxed">
                <strong className="text-white">Européennes premium</strong> : BMW, Audi, Mercedes, Porsche à prix compétitifs. 
                État exceptionnel garanti par la méticulosité des propriétaires coréens.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Véhicules premium à prix réduits</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>État exceptionnel, faibles km</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Inspection locale + dossier photo/vidéo</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <div className="w-2 h-2 bg-neon-lime" />
                  <span>Forfait 3 990€ tout compris</span>
                </li>
              </ul>
              <Link to="/simulateur">
                <Button
                  data-testid="korea-cta"
                  variant="outline"
                  className="w-full border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider py-4 hover:bg-zinc-800 hover:border-neon-lime/50"
                >
                  <span>Simuler un import</span>
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Localisation Section */}
      <section className="py-24 md:py-32 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 border border-neon-lime/50 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-neon-lime" />
                </div>
                <p className="font-mono text-neon-lime text-sm tracking-widest uppercase">
                  Notre Localisation
                </p>
              </div>
              <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-white mb-6">
                Basé en <span className="text-neon-lime">Île-de-France</span>
              </h2>
              <p className="font-manrope text-zinc-400 text-lg leading-relaxed mb-6">
                Situé en Île-de-France, nous profitons des différentes connexions et infrastructures 
                <strong className="text-white"> routières et aériennes</strong>, notamment vers 
                <strong className="text-neon-lime"> l'Allemagne</strong>, premier marché automobile d'Europe, 
                afin d'assurer des <strong className="text-white">délais d'acheminement raisonnables</strong>.
              </p>
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🇩🇪</span>
                  <span className="text-zinc-400">Allemagne</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🇧🇪</span>
                  <span className="text-zinc-400">Belgique</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🇮🇹</span>
                  <span className="text-zinc-400">Italie</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🇳🇱</span>
                  <span className="text-zinc-400">Pays-Bas</span>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 p-8">
              <div className="text-center">
                <span className="text-6xl mb-4 block">🚗</span>
                <p className="font-unbounded text-xl font-bold text-white mb-2">
                  Technicien de maintenance des véhicules
                </p>
                <p className="text-zinc-500 text-sm">
                  Diplômé Bac Pro - Expertise technique certifiée
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Annexes Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              En Supplément
            </p>
            <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-4">
              Services <span className="text-neon-lime">annexes</span>
            </h2>
            <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto">
              Des prestations complémentaires pour une prise en charge complète de votre véhicule.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {servicesAnnexes.map((service, index) => (
              <div
                key={index}
                className="bg-zinc-900 border border-zinc-800 p-6 text-center hover:border-neon-lime/50 transition-colors group"
              >
                <div className="w-14 h-14 border border-zinc-700 group-hover:border-neon-lime/50 flex items-center justify-center mx-auto mb-4 text-zinc-500 group-hover:text-neon-lime transition-colors">
                  {service.icon}
                </div>
                <h3 className="font-unbounded text-sm font-bold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-zinc-500 text-xs leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-zinc-500 text-sm">
              💡 Ces services sont proposés en supplément. Contactez-nous pour un devis personnalisé.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6">
            Une question sur le <span className="text-neon-lime">process</span> ?
          </h2>
          <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto mb-10">
            Je suis disponible pour échanger sur votre projet et répondre à
            toutes vos questions. Premier contact sans engagement.
          </p>
          <Link to="/contact">
            <Button
              data-testid="process-cta"
              className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-10 py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] text-base"
            >
              <span>Contactez-moi</span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
